package or.garden.manager.domain

import io.ktor.client.call.*
import io.ktor.client.request.*
import io.ktor.client.statement.*
import io.ktor.http.*
import karika.distribucija.ba.domain.model.ResultState
import kotlinx.coroutines.CoroutineScope
import kotlinx.serialization.Serializable
import kotlinx.coroutines.Dispatchers
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.coroutines.launch
import or.garden.manager.domain.GardenHttpClient.GARDEN_CLIENT
import or.garden.manager.domain.model.*

class NaturalFertilizerApi {
    private val baseUrl = "${GardenHttpClient.baseUrl}/natural-fertilizers"

    suspend fun getAll(): Result<HttpResponse> = runCatching {
        GARDEN_CLIENT.get(baseUrl)
    }

    suspend fun getById(id: Int): Result<HttpResponse> = runCatching {
        GARDEN_CLIENT.get("$baseUrl/$id")
    }

    suspend fun create(request: CreateNaturalFertilizerRequest): Result<HttpResponse> = runCatching {
        GARDEN_CLIENT.post(baseUrl) {
            setBody(request)
        }
    }

    suspend fun update(id: Int, request: UpdateNaturalFertilizerRequest): Result<HttpResponse> = runCatching {
        GARDEN_CLIENT.put("$baseUrl/$id") {
            setBody(request)
        }
    }

    suspend fun delete(id: Int): Result<HttpResponse> = runCatching {
        GARDEN_CLIENT.delete("$baseUrl/$id")
    }

    suspend fun generateAi(request: AiFertilizerRequest): Result<HttpResponse> = runCatching {
        GARDEN_CLIENT.post("$baseUrl/generate-ai") {
            setBody(request)
        }
    }
}

object NaturalFertilizerRepository {
    private val api = NaturalFertilizerApi()

    fun getAll(): Flow<ResultState<ApiResponse<List<NaturalFertilizer>>>> = flow {
        emit(ResultState.Loading)
        api.getAll().onSuccess { response ->
            if (response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response.bodyAsText()))
            }
        }.onFailure {
            emit(ResultState.Error(it.message ?: "Network error"))
        }
    }

    fun create(request: CreateNaturalFertilizerRequest): Flow<ResultState<ApiResponse<NaturalFertilizer>>> = flow {
        emit(ResultState.Loading)
        api.create(request).onSuccess { response ->
            if (response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response.bodyAsText()))
            }
        }.onFailure {
            emit(ResultState.Error(it.message ?: "Network error"))
        }
    }

    fun update(id: Int, request: UpdateNaturalFertilizerRequest): Flow<ResultState<ApiResponse<String>>> = flow {
        emit(ResultState.Loading)
        api.update(id, request).onSuccess { response ->
            if (response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response.bodyAsText()))
            }
        }.onFailure {
            emit(ResultState.Error(it.message ?: "Network error"))
        }
    }

    fun delete(id: Int): Flow<ResultState<ApiResponse<String>>> = flow {
        emit(ResultState.Loading)
        api.delete(id).onSuccess { response ->
            if (response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                emit(ResultState.Error(response.bodyAsText()))
            }
        }.onFailure {
            emit(ResultState.Error(it.message ?: "Network error"))
        }
    }

    fun generateAi(prompt: String): Flow<ResultState<ApiResponse<AiFertilizerResponse>>> = flow {
        emit(ResultState.Loading)
        api.generateAi(AiFertilizerRequest(prompt)).onSuccess { response ->
            if (response.status.isSuccess()) {
                emit(ResultState.Success(response.body()))
            } else {
                val errorBody = runCatching { response.body<ApiResponse<AiFertilizerResponse>>() }.getOrNull()
                emit(ResultState.Error(errorBody?.message ?: response.bodyAsText()))
            }
        }.onFailure {
            emit(ResultState.Error(it.message ?: "Network error"))
        }
    }
}

object NaturalFertilizerService {
    fun getAll(result: (ResultState<ApiResponse<List<NaturalFertilizer>>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            NaturalFertilizerRepository.getAll().collect { result(it) }
        }
    }

    fun create(request: CreateNaturalFertilizerRequest, result: (ResultState<ApiResponse<NaturalFertilizer>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            NaturalFertilizerRepository.create(request).collect { result(it) }
        }
    }

    fun update(id: Int, request: UpdateNaturalFertilizerRequest, result: (ResultState<ApiResponse<String>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            NaturalFertilizerRepository.update(id, request).collect { result(it) }
        }
    }

    fun delete(id: Int, result: (ResultState<ApiResponse<String>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            NaturalFertilizerRepository.delete(id).collect { result(it) }
        }
    }

    fun generateAi(prompt: String, result: (ResultState<ApiResponse<AiFertilizerResponse>>) -> Unit) {
        CoroutineScope(Dispatchers.Default).launch {
            NaturalFertilizerRepository.generateAi(prompt).collect { result(it) }
        }
    }
}
